<template>
  <div id="app">
    <header class="header">
      <div style="display: flex; justify-content: space-between; align-items: flex-start;">
        <h2 style="margin: 0;">Cosmic Frog</h2>
        <button class="button" @click="handleLogout">
          Logout
        </button>
      </div>
      <div style="margin-top: 4px;">
        <span style="font-size: var(--text-sm); opacity: 0.9;">{{ getUserName() }}</span>
      </div>
    </header>

    <!-- Message Display Div -->
    <div id="messages" style="display: none; color: red; margin-bottom: 10px;"></div>

    <main class="content">
      <LoginScreen v-if="!isLoggedIn" @logged-in="handleLogin" @loggedIn="isLoggedIn = true" />

      <div v-else>
        <component :is="activeComponent" :models="models" :selectedModel="selectedModel" :selectedTable="selectedTable"
          @update:selectedTable="selectedTable = $event" />
      </div>
    </main>
  </div>
</template>

<script setup>

import { computed, onMounted, onBeforeUnmount, ref } from 'vue';
import DefaultDashboard from './components/DefaultDashboard.vue';
import GeocodeData from './components/GeocodeData.vue';
import GetTableData from './components/GetTableData.vue';
import LoginScreen from './components/LoginScreen.vue';
import WorkflowImportData from './components/WorkflowImportData.vue';
import WorkflowExportData from './components/WorkflowExportData.vue';
import WorkflowCreateScenario from './components/WorkflowCreateScenario.vue';
import WorkflowCreateConnectModel from './components/WorkflowCreateConnectModel.vue';
import WorkflowCreateScenarioItem from './components/WorkflowCreateScenarioItem.vue';
import WorkflowRunScenarioModel from './components/WorkflowRunScenarioModel.vue';
import WorkflowRunUtility from './components/WorkflowRunUtility.vue';
import WorkflowUploadFile from './components/WorkflowUploadFile.vue';
import WorkflowDownloadFile from './components/WorkflowDownloadFile.vue';
import WorkflowGeocodeTable from './components/WorkflowGeocodeTable.vue';
import WorkflowRunAction from './components/WorkflowRunAction.vue';
import WorkflowRunDataStarMacro from './components/WorkflowRunDataStarMacro.vue';
import WorkflowRunWorkflow from './components/WorkflowRunWorkflow.vue';
import { getUserName, isAuthenticated, logout, modelList } from './services/optilogicService';
import { showMessage } from './utils/messages';
import { registerChangeHandlers, onCellChange } from './utils/excelUtils';
import { cleanupExcelHandlers } from './composables/useExcelEventHandlers';

const urlParams = new URLSearchParams(window.location.search);
console.log('Current URL: ' + window.location.href);
console.log(urlParams);
const paneId = urlParams.get('pane');
console.log('pane is: ', paneId);

const activeComponent = computed(() => {
  console.log('pane id is: ' + paneId);
  switch (paneId) {
    case 'ExportDataNewPane':
      return WorkflowExportData;
    case 'ImportDataPane':
      return WorkflowImportData;
    case 'CreateScenarioPane':
      return WorkflowCreateScenario;
    case 'CreateConnectModelPane':
      return WorkflowCreateConnectModel;
    case 'CreateScenarioItemPane':
      return WorkflowCreateScenarioItem;
    case 'RunScenarioModelPane':
      return WorkflowRunScenarioModel;
    case 'RunUtilityPane':
      return WorkflowRunUtility;
    case 'UploadFilePane':
      return WorkflowUploadFile;
    case 'DownloadFilePane':
      return WorkflowDownloadFile;
    case 'GeocodeTablePane':
      return WorkflowGeocodeTable;
    case 'RunActionPane':
      return WorkflowRunAction;
    case 'RunWorkflowPane':
      return WorkflowRunWorkflow;
    case 'RunDataStarMacroPane':
      return WorkflowRunDataStarMacro;
    case 'GetDataPane':
      return GetTableData;
    case 'GeocodeDataPane':
      return GeocodeData;
    default:
      return DefaultDashboard;
  }
});




// Reactive state variables
const isLoggedIn = ref(false); // Tracks login status
const models = ref([]); // Holds the models for the dropdown
const selectedModel = ref(''); // Tracks selected model
const selectedTable = ref(''); // Tracks selected table


const fetchModelList = async () => {
  const modelData = await modelList(); // Fetch filtered models
  models.value = modelData; // Assign data to reactive variable
  console.log("Models Loaded:", models.value); // Debug log
};

const setupExcelHandlers = async () => {
  try {
    await registerChangeHandlers();
    await onCellChange();
    console.log("Excel handlers registered");
  } catch (error) {
    console.error("Error setting up Excel handlers:", error);
  }
};

const handleLogin = async () => {
  isLoggedIn.value = true;
  await fetchModelList();
  await setupExcelHandlers(); // Register Excel handlers once after login
};

// Logout function
const handleLogout = async () => {
  await cleanupExcelHandlers(); // Clean up handlers on logout
  logout();
  isLoggedIn.value = false;
  models.value = []; // Clear the model list
  selectedModel.value = null;
  selectedTable.value = '';
  showMessage('Logged out successfully.');
};

// Initialize state based on sessionStorage
onMounted(async () => {
  isLoggedIn.value = isAuthenticated();
  if (isLoggedIn.value) {
    try {
      await fetchModelList();
      await setupExcelHandlers(); // Set up handlers if already logged in
    } catch (error) {
      console.error("Error loading models:", error);
      showMessage("Failed to load models.");
    }
  }
});

// Clean up handlers when app unmounts
onBeforeUnmount(async () => {
  if (isLoggedIn.value) {
    await cleanupExcelHandlers();
  }
});
</script>



<style>
html,
body {
  background-color: var(--color-ds-backdrop);
  font-family: var(--font-roboto);
  margin: 0;
  padding: 0;
}

.header {
  background-color: var(--color-ds-primary);
  color: white;
  text-align: center;
  padding: 10px;
  font-size: var(--text-lg);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.header > div:first-child {
  display: flex;
  align-items: center;
  gap: 8px;
}

.content {
  padding: 20px;
  color: var(--color-ds-gray);
}

/* Button styles are now in design-tokens.css */

.icon {
  margin-right: 10px;
  width: 24px;
  height: 24px;
}
</style>